import React, { useCallback, useEffect, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { Extension, Mark, mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { Link } from '@tiptap/extension-link';
import { Underline } from '@tiptap/extension-underline';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { Image } from '@tiptap/extension-image';
import {
  faBold,
  faEraser,
  faImage,
  faItalic,
  faLink,
  faListOl,
  faListUl,
  faRedo,
  faStrikethrough,
  faSubscript,
  faSuperscript,
  faUnderline,
  faUndo,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'reactstrap';
import CustomOption from './CustomButtons';
import { normalizeLinkHref } from './Wysiwyg.helpers';
import { sanitizePastedHtml } from './Wysiwyg.paste';
import './Wysiwyg.style.css';

const normalizeHtml = (html = '') => {
  if (!html || typeof html !== 'string' || html.trim().length === 0) {
    return '<p></p>';
  }

  return html;
};

let typeTimer = null;

const FONT_FAMILY_OPTIONS = [
  { value: '', label: 'Font' },
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: '"Helvetica Neue", Helvetica, Arial, sans-serif', label: 'Helvetica' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: '"Times New Roman", Times, serif', label: 'Times New Roman' },
  { value: '"Courier New", Courier, monospace', label: 'Courier New' },
];

const FONT_SIZE_OPTIONS = [
  { value: '', label: 'Size' },
  { value: '12px', label: '12' },
  { value: '14px', label: '14' },
  { value: '16px', label: '16' },
  { value: '18px', label: '18' },
  { value: '24px', label: '24' },
];

const FontFamily = Extension.create({
  name: 'fontFamily',

  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontFamily: {
            default: null,
            parseHTML: (element) => element.style.fontFamily || null,
            renderHTML: (attributes) => {
              if (!attributes.fontFamily) {
                return {};
              }

              return {
                style: `font-family: ${attributes.fontFamily}`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontFamily:
        (fontFamily) =>
        ({ chain }) =>
          chain().setMark('textStyle', { fontFamily }).run(),
      unsetFontFamily:
        () =>
        ({ chain }) =>
          chain().setMark('textStyle', { fontFamily: null }).removeEmptyTextStyle().run(),
    };
  },
});

const FontSize = Extension.create({
  name: 'fontSize',

  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize || null,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) {
                return {};
              }

              return {
                style: `font-size: ${attributes.fontSize}`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontSize:
        (fontSize) =>
        ({ chain }) =>
          chain().setMark('textStyle', { fontSize }).run(),
      unsetFontSize:
        () =>
        ({ chain }) =>
          chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
    };
  },
});

const Subscript = Mark.create({
  name: 'subscript',
  excludes: 'superscript',

  parseHTML() {
    return [{ tag: 'sub' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['sub', mergeAttributes(HTMLAttributes), 0];
  },

  addCommands() {
    return {
      toggleSubscript:
        () =>
        ({ commands }) =>
          commands.toggleMark(this.name),
    };
  },
});

const Superscript = Mark.create({
  name: 'superscript',
  excludes: 'subscript',

  parseHTML() {
    return [{ tag: 'sup' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['sup', mergeAttributes(HTMLAttributes), 0];
  },

  addCommands() {
    return {
      toggleSuperscript:
        () =>
        ({ commands }) =>
          commands.toggleMark(this.name),
    };
  },
});

const TabKeyBehavior = Extension.create({
  name: 'tabKeyBehavior',

  addKeyboardShortcuts() {
    return {
      Tab: () => {
        if (this.editor.isActive('listItem')) {
          return this.editor.commands.sinkListItem('listItem');
        }

        return this.editor.commands.insertContent('\t');
      },
      'Shift-Tab': () => {
        if (this.editor.isActive('listItem')) {
          return this.editor.commands.liftListItem('listItem');
        }

        return false;
      },
    };
  },
});

const Wysiwyg = ({
  html,
  onHtmlChange,
  selectedCandidate,
  setEditorFocusHandler,
  isUpdating,
  toggleAttachmentPopup,
  forcePlainText,
}) => {
  const [currentHtml, setCurrentHtml] = useState(normalizeHtml(html));
  const wrapperRef = useRef(null);
  const latestHtmlRef = useRef(currentHtml);
  const isApplyingExternalRef = useRef(false);
  const lastSelectionRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: false,
        underline: false,
      }),
      TextStyle,
      FontFamily,
      FontSize,
      Color.configure({ types: ['textStyle'] }),
      Underline,
      Subscript,
      Superscript,
      TabKeyBehavior,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      Image,
    ],
    editorProps: {
      attributes: {
        role: 'textbox',
        'aria-label': 'Message body',
        'aria-multiline': 'true',
      },
      // Runs for every paste, including editor.view.pasteHTML below, so Word
      // and Outlook markup is cleaned up in one place.
      transformPastedHTML: (pastedHtml) => sanitizePastedHtml(pastedHtml),
      handleDOMEvents: {
        keydown: (_view, event) => {
          // accessiBe can treat Space as an "activate" key in keyboard navigation mode.
          // If their handler runs on bubble, keep Space from leaving the editor so typing still works.
          if (event.key === ' ' || event.code === 'Space') {
            event.stopPropagation();
            if (typeof event.stopImmediatePropagation === 'function') {
              event.stopImmediatePropagation();
            }
          }

          if (event.key === 'Tab') {
            event.stopPropagation();
          }

          return false;
        },
        keypress: (_view, event) => {
          if (event.key === ' ' || event.code === 'Space') {
            event.stopPropagation();
            if (typeof event.stopImmediatePropagation === 'function') {
              event.stopImmediatePropagation();
            }
          }

          return false;
        },
        keyup: (_view, event) => {
          if (event.key === ' ' || event.code === 'Space') {
            event.stopPropagation();
            if (typeof event.stopImmediatePropagation === 'function') {
              event.stopImmediatePropagation();
            }
          }

          return false;
        },
      },
    },
    content: normalizeHtml(currentHtml),
    editable: !isUpdating,
    onCreate: ({ editor: currentEditor }) => {
      const { from, to } = currentEditor.state.selection;
      lastSelectionRef.current = { from, to };
    },
    onSelectionUpdate: ({ editor: currentEditor }) => {
      const { from, to } = currentEditor.state.selection;
      lastSelectionRef.current = { from, to };
    },
    onUpdate: ({ editor: currentEditor }) => {
      if (isApplyingExternalRef.current) {
        return;
      }

      const html = normalizeHtml(currentEditor.getHTML());
      latestHtmlRef.current = html;
      setCurrentHtml(html);
    },
  });

  const applyHtmlToEditor = useCallback(
    (newHtml = '') => {
      if (!editor) {
        return;
      }

      const normalizedHtml = normalizeHtml(newHtml);

      if (normalizedHtml === latestHtmlRef.current) {
        return;
      }

      isApplyingExternalRef.current = true;
      editor.commands.setContent(normalizedHtml, false);
      isApplyingExternalRef.current = false;

      latestHtmlRef.current = normalizedHtml;
      setCurrentHtml(normalizedHtml);
    },
    [editor],
  );

  useEffect(() => {
    if (editor) {
      editor.setEditable(!isUpdating);
    }
  }, [editor, isUpdating]);

  useEffect(() => {
    if (!setEditorFocusHandler) {
      return;
    }

    setEditorFocusHandler(() => () => {
      if (!editor || !editor.isEditable) {
        return false;
      }

      editor.chain().focus().run();
      return true;
    });

    return () => {
      setEditorFocusHandler(null);
    };
  }, [editor, setEditorFocusHandler]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    applyHtmlToEditor(normalizeHtml(html));
  }, [html, editor, applyHtmlToEditor]);

  useEffect(() => {
    if (!onHtmlChange) {
      return;
    }

    clearTimeout(typeTimer);
    typeTimer = setTimeout(() => {
      onHtmlChange(currentHtml);
    }, 300);

    return () => {
      clearTimeout(typeTimer);
    };
  }, [currentHtml, onHtmlChange]);

  const handleCopy = (event) => {
    if (!editor || !wrapperRef.current || editor.state.selection.empty) {
      return;
    }

    const selection = window.getSelection();

    if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
      return;
    }

    const anchorNode = selection.anchorNode;
    if (anchorNode && !wrapperRef.current.contains(anchorNode)) {
      return;
    }

    const clipboard = editor.view.serializeForClipboard(editor.state.selection.content());
    const container = document.createElement('div');
    container.appendChild(clipboard.dom);

    const selectedHtml = container.innerHTML;
    const selectedText = clipboard.text;

    if (!selectedText) {
      return;
    }

    // The custom type keeps an internal paste byte-exact; text/html is what
    // Outlook, Gmail and Word read, and without it a copy out of the editor
    // pastes as unformatted plain text.
    event.clipboardData.setData('application/x-rezrate-editor-html', selectedHtml);
    event.clipboardData.setData('text/html', selectedHtml);
    event.clipboardData.setData('text/plain', selectedText);
    event.preventDefault();
  };

  const handlePaste = (event) => {
    if (!editor) {
      return;
    }

    const customHtml = event.clipboardData.getData('application/x-rezrate-editor-html');

    if (customHtml) {
      event.preventDefault();
      editor.view.pasteHTML(customHtml, event.nativeEvent);
      return;
    }

    if (forcePlainText) {
      const plainText = event.clipboardData.getData('text/plain');
      if (plainText) {
        event.preventDefault();
        editor.chain().focus().insertContent(plainText.replace(/\r\n/g, '\n')).run();
      }
    }
  };

  const toggleLink = () => {
    if (!editor) {
      return;
    }

    const previousUrl = editor.getAttributes('link').href || '';
    const newUrl = window.prompt('Enter a link URL', previousUrl);

    if (newUrl === null) {
      return;
    }

    if (newUrl.trim() === '') {
      runWithSelection((chain) => chain.unsetLink());
      return;
    }

    const normalizedUrl = normalizeLinkHref(newUrl);

    if (!normalizedUrl) {
      window.alert('Please enter a valid web, email, or telephone link.');
      return;
    }

    runWithSelection((chain) =>
      chain.setLink({
        href: normalizedUrl,
        target: '_blank',
        rel: 'noopener noreferrer',
      }),
    );
  };

  const insertImage = () => {
    if (!editor) {
      return;
    }

    const url = window.prompt('Enter image URL');

    if (!url || url.trim() === '') {
      return;
    }

    runWithSelection((chain) => chain.setImage({ src: url.trim() }), { preserveSelection: false });
  };

  const captureSelection = () => {
    if (!editor) {
      return;
    }

    const { from, to } = editor.state.selection;
    lastSelectionRef.current = { from, to };
  };

  const runWithSelection = (command, options = {}) => {
    if (!editor || !command) {
      return;
    }

    const { preserveSelection = true } = options;
    const selection = lastSelectionRef.current;
    const hasSelection =
      selection &&
      Number.isInteger(selection.from) &&
      Number.isInteger(selection.to) &&
      selection.from <= selection.to;

    let chain = editor.chain().focus();

    if (hasSelection) {
      chain = chain.setTextSelection(selection);
    }

    chain = command(chain);

    if (preserveSelection && hasSelection) {
      chain = chain.setTextSelection(selection);
    }

    chain.run();
  };

  const setBlockStyle = (value) => {
    if (!editor) {
      return;
    }

    if (value === 'paragraph') {
      runWithSelection((chain) => chain.setParagraph());
      return;
    }

    if (value === 'h1' || value === 'h2' || value === 'h3') {
      runWithSelection((chain) => chain.setHeading({ level: Number(value.slice(1)) }));
    }
  };

  const currentBlockStyle = editor.isActive('heading', { level: 1 })
    ? 'h1'
    : editor.isActive('heading', { level: 2 })
      ? 'h2'
      : editor.isActive('heading', { level: 3 })
        ? 'h3'
        : 'paragraph';

  const currentTextStyleAttributes = editor.getAttributes('textStyle');
  const currentFontFamily = currentTextStyleAttributes.fontFamily || '';
  const currentFontSize = currentTextStyleAttributes.fontSize || '';

  const renderToolbarButton = ({ icon, label, isActive, onClick, disabled }) => {
    return (
      <Button
        size="sm"
        color="light"
        outline={!isActive}
        className={`tiptap-btn${isActive ? ' is-active' : ''}`}
        onMouseDown={(event) => {
          event.preventDefault();
          captureSelection();
        }}
        onClick={onClick}
        disabled={disabled}
        title={label}
      >
        <FontAwesomeIcon icon={icon} />
      </Button>
    );
  };

  if (!editor) {
    return <div style={{ minHeight: '250px' }} />;
  }

  return (
    <div className="tiptap-shell">
      <div ref={wrapperRef} onCopy={handleCopy} onPasteCapture={handlePaste} className="tiptap-wrapper">
        <div className="tiptap-toolbar">
          <div className="tiptap-toolbar-group tiptap-toolbar-group--select">
            <select
              className="tiptap-select"
              value={currentBlockStyle}
              onMouseDown={captureSelection}
              onChange={(e) => {
                setBlockStyle(e.target.value);
              }}
              aria-label="Text style"
            >
              <option value="paragraph">Normal</option>
              <option value="h1">Heading 1</option>
              <option value="h2">Heading 2</option>
              <option value="h3">Heading 3</option>
            </select>
            <select
              className="tiptap-select"
              value={currentFontFamily}
              onMouseDown={captureSelection}
              onChange={(e) => {
                if (e.target.value) {
                  runWithSelection((chain) => chain.setFontFamily(e.target.value));
                  return;
                }

                runWithSelection((chain) => chain.unsetFontFamily());
              }}
              aria-label="Font family"
            >
              {FONT_FAMILY_OPTIONS.map((option) => (
                <option key={option.label} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <select
              className="tiptap-select tiptap-select--small"
              value={currentFontSize}
              onMouseDown={captureSelection}
              onChange={(e) => {
                if (e.target.value) {
                  runWithSelection((chain) => chain.setFontSize(e.target.value));
                  return;
                }

                runWithSelection((chain) => chain.unsetFontSize());
              }}
              aria-label="Font size"
            >
              {FONT_SIZE_OPTIONS.map((option) => (
                <option key={option.label} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="tiptap-toolbar-group">
            {renderToolbarButton({
              icon: faUndo,
              label: 'Undo',
              isActive: false,
              disabled: !editor.can().chain().focus().undo().run(),
              onClick: (e) => {
                e.preventDefault();
                runWithSelection((chain) => chain.undo(), { preserveSelection: false });
              },
            })}
            {renderToolbarButton({
              icon: faRedo,
              label: 'Redo',
              isActive: false,
              disabled: !editor.can().chain().focus().redo().run(),
              onClick: (e) => {
                e.preventDefault();
                runWithSelection((chain) => chain.redo(), { preserveSelection: false });
              },
            })}
          </div>

          <div className="tiptap-toolbar-group">
            {renderToolbarButton({
              icon: faBold,
              label: 'Bold',
              isActive: editor.isActive('bold'),
              onClick: (e) => {
                e.preventDefault();
                runWithSelection((chain) => chain.toggleBold());
              },
            })}
            {renderToolbarButton({
              icon: faItalic,
              label: 'Italic',
              isActive: editor.isActive('italic'),
              onClick: (e) => {
                e.preventDefault();
                runWithSelection((chain) => chain.toggleItalic());
              },
            })}
            {renderToolbarButton({
              icon: faUnderline,
              label: 'Underline',
              isActive: editor.isActive('underline'),
              onClick: (e) => {
                e.preventDefault();
                runWithSelection((chain) => chain.toggleUnderline());
              },
            })}
            {renderToolbarButton({
              icon: faStrikethrough,
              label: 'Strikethrough',
              isActive: editor.isActive('strike'),
              onClick: (e) => {
                e.preventDefault();
                runWithSelection((chain) => chain.toggleStrike());
              },
            })}
            {renderToolbarButton({
              icon: faSuperscript,
              label: 'Superscript',
              isActive: editor.isActive('superscript'),
              onClick: (e) => {
                e.preventDefault();
                runWithSelection((chain) => chain.toggleSuperscript());
              },
            })}
            {renderToolbarButton({
              icon: faSubscript,
              label: 'Subscript',
              isActive: editor.isActive('subscript'),
              onClick: (e) => {
                e.preventDefault();
                runWithSelection((chain) => chain.toggleSubscript());
              },
            })}
          </div>

          <div className="tiptap-toolbar-group">
            {renderToolbarButton({
              icon: faListUl,
              label: 'Bulleted list',
              isActive: editor.isActive('bulletList'),
              onClick: (e) => {
                e.preventDefault();
                runWithSelection((chain) => chain.toggleBulletList());
              },
            })}
            {renderToolbarButton({
              icon: faListOl,
              label: 'Numbered list',
              isActive: editor.isActive('orderedList'),
              onClick: (e) => {
                e.preventDefault();
                runWithSelection((chain) => chain.toggleOrderedList());
              },
            })}
          </div>

          <div className="tiptap-toolbar-group">
            {renderToolbarButton({
              icon: faLink,
              label: 'Insert link',
              isActive: editor.isActive('link'),
              onClick: (e) => {
                e.preventDefault();
                toggleLink();
              },
            })}
            {renderToolbarButton({
              icon: faImage,
              label: 'Insert image',
              isActive: false,
              onClick: (e) => {
                e.preventDefault();
                insertImage();
              },
            })}
            <label className="tiptap-color-wrapper" htmlFor="tiptapColorInput">
              <span className="tiptap-color-label">A</span>
              <input
                id="tiptapColorInput"
                className="tiptap-color-input"
                type="color"
                value={currentTextStyleAttributes.color || '#1f2937'}
                onMouseDown={captureSelection}
                onChange={(e) => {
                  runWithSelection((chain) => chain.setColor(e.target.value));
                }}
              />
            </label>
            {renderToolbarButton({
              icon: faEraser,
              label: 'Clear formatting',
              isActive: false,
              onClick: (e) => {
                e.preventDefault();
                runWithSelection((chain) => chain.unsetAllMarks().clearNodes());
              },
            })}
          </div>
        </div>
          <div className="tiptap-token-bar">
            <div className="tiptap-token-title">Merge Variables</div>
            <CustomOption
              html={currentHtml}
              onInsertAttachment={({ text, url }) => {
                editor.chain().focus().insertContent({
                  marks: [{ type: 'link', attrs: { href: url, target: '_blank' } }],
                  text,
                  type: 'text',
                }).run();
              }}
              onInsertText={(text) => {
                editor.chain().focus().insertContent(text).run();
              }}
              selectedCandidate={selectedCandidate}
              toggleAttachmentPopup={toggleAttachmentPopup}
            />
          </div>
        <div className="tiptap-editor-surface">
          <EditorContent editor={editor} className="tiptap-editor-content" />
        </div>
      </div>
    </div>
  );
};

export default Wysiwyg;
